import type { SettingsPreviewResponse, Sizing } from '../../api';

/** An i18n key of `settings.preview` and its values: the line naming the example's instrument. */
export interface ReferenceLine {
  readonly key:
    | 'settings.preview.referenceChannel'
    | 'settings.preview.referenceDefault'
    | 'settings.preview.referenceNotPriced'
    | 'settings.preview.referenceChannelRisk'
    | 'settings.preview.referenceDefaultRisk'
    | 'settings.preview.referenceNotPricedRisk';
  readonly values: { readonly symbol: string; readonly channelSymbol?: string };
}

/**
 * The line that names the instrument of the worked example (P9 R1–R3; Q32, Q258, provisional):
 *
 * - `CHANNEL`: the channel's most frequent instrument, said as such;
 * - `DEFAULT`: EURUSD, because the channel has no signal yet;
 * - `CHANNEL_INSTRUMENT_NOT_PRICED`: EURUSD, because the account gives no price for the channel's
 *   instrument, which is named so the trader knows the example is not on it.
 *
 * The API sends codes and symbols only; the words are the Mini App's (I12). The contract makes
 * `channelSymbol` null exactly for `DEFAULT`, so the default line is also the safe one should it
 * ever be missing.
 *
 * In risk % the line adds that the amount is the same on any instrument (P9 D3, Proposal, to be
 * validated): equity × % does not depend on it (M2 R5), so the instrument named must not read as
 * a reason for the amount.
 */
export function referenceLine(
  preview: SettingsPreviewResponse,
  mode: Sizing['mode'],
): ReferenceLine {
  const symbol = preview.referenceSymbol;
  const risk = mode === 'RISK_PERCENT';
  if (preview.referenceSource === 'DEFAULT' || preview.channelSymbol === null) {
    return {
      key: risk ? 'settings.preview.referenceDefaultRisk' : 'settings.preview.referenceDefault',
      values: { symbol },
    };
  }
  if (preview.referenceSource === 'CHANNEL_INSTRUMENT_NOT_PRICED') {
    return {
      key: risk ? 'settings.preview.referenceNotPricedRisk' : 'settings.preview.referenceNotPriced',
      values: { symbol, channelSymbol: preview.channelSymbol },
    };
  }
  return {
    key: risk ? 'settings.preview.referenceChannelRisk' : 'settings.preview.referenceChannel',
    values: { symbol },
  };
}
